import { computed, ref, shallowRef, type Ref } from 'vue'
import { DRAW_POLYGON_OPTIONS, SHOW_POLYGON_OPTIONS } from './constant'

/**
 * 多边形路径类型
 */
type PolygonPaths = LngLatPoint[][]

/**
 * 地理围栏管理 Hook
 * 负责多边形的创建、编辑、删除和同步显示
 */
export function useTMapGeofence(Map: Ref<any>, value: Ref<string | undefined>) {
  const T = window.T

  // 存储所有多边形实例
  const polygons = shallowRef<any[]>([])

  // 正在编辑的多边形索引
  const editIndex = ref<number | null>(null)

  // 解析围栏数据
  const paths = computed<PolygonPaths>(() => {
    if (!value.value) return []
    try {
      return JSON.parse(value.value)
    } catch {
      return []
    }
  })

  /**
   * 创建多边形对象
   */
  const createPolygon = (points: LngLatPoint[], index: number, options = SHOW_POLYGON_OPTIONS) => {
    const polygon = new T.Polygon(points, options)
    // 存储索引，用于后续操作识别
    polygon.__idx = index
    return polygon
  }

  /**
   * 调整地图视野以适应所有多边形
   * 确保所有多边形都在可视范围内
   */
  const zoomToFit = () => {
    if (!Map.value || polygons.value.length === 0) return

    const allPoints: any[] = []
    polygons.value.forEach(polygon => {
      const [points = []] = polygon.getLngLats()
      allPoints.push(...points)
    })

    if (allPoints.length > 0) {
      Map.value.setViewport(allPoints)
    }
  }

  /**
   * 地图居中到指定多边形
   */
  const zoomToPolygon = (index: number) => {
    if (!Map.value || !paths.value[index]) return
    Map.value.setViewport(paths.value[index])
  }

  /**
   * 同步多边形数据到地图显示
   * 采用增量更新策略，避免全部重绘
   */
  const syncPolygons = (pathsData: PolygonPaths, skipEditCheck = false) => {
    if (!Map.value) return

    // 1. 删除多余的多边形
    for (let i = polygons.value.length - 1; i >= pathsData.length; i--) {
      const polygon = polygons.value[i]
      Map.value.removeOverLay(polygon)
      polygons.value.pop()
    }

    // 2. 更新已有的多边形或新增缺少的多边形
    for (let i = 0; i < pathsData.length; i++) {
      const points = pathsData[i]
      const polygon = polygons.value[i]

      if (polygon) {
        // 更新已存在的多边形
        polygon.__idx = i
        // 避免正在编辑时被外部同步打断
        const isEditing = !skipEditCheck && editIndex.value === i
        if (!isEditing && typeof polygon.setLngLats === 'function') {
          polygon.setLngLats([points])
        }
      } else {
        // 新增多边形
        const newPolygon = createPolygon(points, i)
        polygons.value.push(newPolygon)
        Map.value.addOverLay(newPolygon)
      }
    }
  }

  /**
   * 开始编辑指定多边形
   *
   * @param index - 多边形索引
   */
  const startEdit = (index: number) => {
    if (polygons.value[index]) {
      editIndex.value = index
      zoomToPolygon(index)
      polygons.value[index].enableEdit()
    }
  }

  /**
   * 结束编辑并获取更新后的坐标
   *
   * @param index - 多边形索引
   * @returns 更新后的所有多边形路径数据（JSON字符串）
   */
  const finishEdit = (index: number): string => {
    if (polygons.value[index]) {
      polygons.value[index].disableEdit()
      const updatedPaths = paths.value.map((item, i) => (i === index ? polygons.value[index].getLngLats()[0] : item))
      editIndex.value = null
      return JSON.stringify(updatedPaths)
    }
    return JSON.stringify(paths.value)
  }

  /**
   * 删除指定多边形
   *
   * @param index - 多边形索引
   * @returns 删除后的所有多边形路径数据（JSON字符串）
   */
  const deletePolygon = (index: number): string => {
    const newPaths = paths.value.filter((_, i) => i !== index)
    return JSON.stringify(newPaths)
  }

  /**
   * 添加新的多边形
   *
   * @param points - 新多边形的顶点坐标数组
   * @returns 添加后的所有多边形路径数据（JSON字符串）
   */
  const addPolygon = (points: LngLatPoint[]): string => {
    const newPaths = [...paths.value, points]
    return JSON.stringify(newPaths)
  }

  /**
   * 清除所有多边形
   */
  const clearPolygons = () => {
    if (!Map.value) return
    polygons.value.forEach(polygon => {
      Map.value.removeOverLay(polygon)
    })
    polygons.value.length = 0
  }

  /**
   * 初始化多边形绘制工具
   *
   * @param onDrawComplete - 绘制完成回调
   * @returns 绘制工具实例
   */
  const initDrawTool = (onDrawComplete: (points: LngLatPoint[]) => void) => {
    if (!Map.value) return null

    const polygonTool = new T.PolygonTool(Map.value, DRAW_POLYGON_OPTIONS)
    polygonTool.setTips('双击结束绘制')

    polygonTool.addEventListener('draw', (res: any) => {
      onDrawComplete(res.currentLnglats)
      polygonTool.clear()
    })

    return polygonTool
  }

  return {
    paths,
    polygons,
    editIndex,
    createPolygon,
    zoomToFit,
    zoomToPolygon,
    syncPolygons,
    startEdit,
    finishEdit,
    deletePolygon,
    addPolygon,
    clearPolygons,
    initDrawTool
  }
}
